import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Users } from "lucide-react";
import {
  ConversationPriority,
  ConversationStatus,
} from "@lytra/frontend-common/__generated__/graphql";
import type { InfoField } from "@/components/customer/ConversationHeader";
import { Select, SelectTrigger } from "@/components/ui/select";
import {
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
} from "@lytra/frontend-common/components/v2/ui/dropdown-menu";
import { PersonAvatar } from "@/components/PersonAvatar";
import type { WorkingAgent } from "@/hooks/useAgentPresence";
import { KBD_CLASS } from "@/lib/kbd";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { SelectContent } from "@lytra/frontend-common/components/v2/ui/select";
import { SplitButton } from "@lytra/frontend-common/components/v2/ui/split-button";
import { CONVERSATION_PRIORITY_SOLID_COLOR_MAP } from "@/consts/conversation";
import { cn, getAttachmentIcon, Person } from "@/lib/utils";
import { choiceOf, statusChoices, useTicketStatuses } from "./ticketStatuses";
import { statusColorClasses } from "./ticketStatusColors";
import {
  assignToMeValue,
  contactLine,
  formatFileSize,
  isTeamValue,
  PRIORITY_ORDER,
  priorityOptionLabel,
  statusLabel,
} from "./ticketHelpers";
import {
  AssigneeMenuItems,
  AssigneeSelectItems,
  DotSelectItems,
} from "./TicketSelectItems";
import CollapsibleItems from "./CollapsibleItems";
import SlaBadge, { type TicketSlaView } from "./SlaBadge";

interface TicketMetaHeaderProps {
  contact?: (Person & { emailAddress?: string | null }) | null;
  companyName?: string | null;
  title?: string | null;
  fields: InfoField[];
  status: ConversationStatus;
  priority: ConversationPriority;
  attachments: {
    id: string;
    name: string;
    size: number;
    contentType: string;
  }[];
  onDownloadAttachment: (attachmentId: string) => void;
  /** The ticket's SLA clock (#888); null when no policy covers it. */
  sla?: TicketSlaView | null;
  /** The viewer: the assignee chip's main part assigns the ticket to this account. */
  currentUserId?: string;
  /** A colleague working on the answer right now: one line under the chips while set.
   * Set only with groupware presence on, which also offers "Mir zuweisen" (key I) first
   * in the assignee picker. */
  working?: WorkingAgent | null;
  presence?: boolean;
  /** Shown above the ticket's columns rather than on top of the thread: the full title
   * wraps, and the surrounding slot draws the dividing line. */
  standalone?: boolean;
}

const CHIP_BASE =
  "inline-flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[10.5px] font-bold whitespace-nowrap";

const EDIT_TRIGGER =
  "h-[22px] w-auto gap-1 rounded-full border-0 px-2.5 py-0 text-[10.5px] font-bold shadow-none focus:ring-2 focus:ring-secondary/40 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-80 [&>span]:line-clamp-none [&>span]:!inline-flex";

function MetaBlock({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[9.6px] font-bold uppercase tracking-[.04em] text-muted-foreground">
        {label}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

/** A chip that becomes a Select when the field is available, else a read-only chip. */
function EditableChip({
  field,
  options,
  dotClassFor,
  className,
  children,
}: {
  field?: InfoField;
  options?: InfoField["options"];
  /** Colour of the dot shown before each option, so the dropdown previews the chip colour. */
  dotClassFor?: (value: string) => string | undefined;
  className: string;
  children: ReactNode;
}) {
  if (!field?.options || !field.onSelect) {
    return <span className={cn(CHIP_BASE, className)}>{children}</span>;
  }
  const items = options ?? field.options;
  return (
    <Select
      value={field.valueId ?? ""}
      onValueChange={(value) => field.onSelect?.(value)}
    >
      <SelectTrigger className={cn(EDIT_TRIGGER, className)}>
        <span className="inline-flex items-center gap-1.5">{children}</span>
      </SelectTrigger>
      {/* The v2 list: its height follows its rows (up to 320px). The old one was capped at
          the 22px chip's height, so it showed a scroll arrow that did nothing on click. */}
      <SelectContent>
        {dotClassFor ? (
          <DotSelectItems options={items} dotClassFor={dotClassFor} />
        ) : (
          <AssigneeSelectItems options={items} />
        )}
      </SelectContent>
    </Select>
  );
}

/** The assignee as a split chip (#960): the wide part shows the owner and assigns the ticket to
 * the viewer in one click ("Mir zuweisen"), the chevron opens the person-or-team picker with the
 * owner checked. Only rendered while assigning oneself changes something (`assignToMe`). */
function AssigneeSplitChip({
  field,
  assignToMe,
  ownerName,
  withShortcut,
  children,
}: {
  field: InfoField;
  /** The picker value that assigns the viewer (assignToMeValue). */
  assignToMe: string;
  ownerName: string;
  /** Lists "Mir zuweisen" with its key first in the picker. */
  withShortcut: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <SplitButton
      size="chip"
      variant="outline"
      title={t("tickets.meta.assignToMe")}
      aria-label={t("tickets.meta.assignToMeLabel", { name: ownerName })}
      menuLabel={t("tickets.meta.chooseAssignee")}
      menuAlign="start"
      onClick={() => field.onSelect?.(assignToMe)}
      menu={
        <>
          {withShortcut && (
            <>
              <DropdownMenuItem inset onSelect={() => field.onSelect?.(assignToMe)}>
                {t("tickets.meta.assignToMe")}
                <DropdownMenuShortcut>
                  <kbd className={KBD_CLASS}>I</kbd>
                </DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuRadioGroup
            value={field.valueId ?? ""}
            onValueChange={(value) => field.onSelect?.(value)}
          >
            <AssigneeMenuItems
              options={field.options ?? []}
              selected={field.valueId ?? ""}
            />
          </DropdownMenuRadioGroup>
        </>
      }
    >
      <span className="inline-flex items-center gap-1">{children}</span>
    </SplitButton>
  );
}

export default function TicketMetaHeader({
  contact,
  companyName,
  title,
  fields,
  status,
  priority,
  attachments,
  onDownloadAttachment,
  sla,
  currentUserId,
  working = null,
  presence = false,
  standalone = false,
}: TicketMetaHeaderProps) {
  const { t } = useTranslation();
  const statusField = fields.find((field) => field.id === "status");
  const priorityField = fields.find((field) => field.id === "priority");
  const assigneeField = fields.find((field) => field.id === "assignee");

  const senderLine = contactLine(t, contact, companyName);

  // The status's own colour (ADR-0078), read off the picked choice.
  const choices = statusChoices(useTicketStatuses());
  const statusStyle = statusColorClasses(
    choiceOf(choices, statusField?.valueId)?.color,
  );
  const assigneeName = assigneeField?.value?.trim();
  const ownerName = assigneeName ? assigneeName : t("tickets.meta.unassigned");
  // Null when the ticket is already the viewer's own (or the field is read-only): then the
  // chip is the plain picker, so no part of it is a button that does nothing.
  const assignToMe =
    assigneeField?.options && assigneeField.onSelect
      ? assignToMeValue(
          assigneeField.options,
          assigneeField.valueId,
          currentUserId,
        )
      : null;
  const ownerLabel = (
    <>
      {isTeamValue(assigneeField?.valueId) && <Icon icon={Users} size={12} />}
      {ownerName}
    </>
  );

  return (
    <header className={standalone ? undefined : "border-b"}>
      <div className="flex items-start gap-3 px-4 pt-3.5 pb-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-semibold text-muted-foreground">
            {senderLine}
          </div>
          <div
            className={cn(
              "mt-0.5 text-base font-bold leading-[1.32]",
              !standalone && "line-clamp-2",
            )}
          >
            {title ?? t("tickets.meta.noSubject")}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-x-[18px] gap-y-2 px-4 pb-3">
        <MetaBlock label={t("tickets.meta.status")}>
          <EditableChip
            field={statusField}
            className={statusStyle.chip}
            dotClassFor={(value) =>
              statusColorClasses(choiceOf(choices, value)?.color).dot
            }
          >
            <span
              className={cn("h-[7px] w-[7px] rounded-full", statusStyle.dot)}
            />
            {statusField?.value ?? statusLabel(t, status)}
          </EditableChip>
        </MetaBlock>

        <MetaBlock label={t("tickets.meta.priority")}>
          <EditableChip
            field={priorityField}
            dotClassFor={(value) =>
              CONVERSATION_PRIORITY_SOLID_COLOR_MAP[
                value as ConversationPriority
              ]
            }
            options={PRIORITY_ORDER.map((value) => ({
              value,
              label: priorityOptionLabel(t, value),
            }))}
            className={cn(
              CONVERSATION_PRIORITY_SOLID_COLOR_MAP[priority],
              "text-white",
            )}
          >
            {priorityOptionLabel(t, priority)}
          </EditableChip>
        </MetaBlock>

        {sla && (
          <MetaBlock label={t("tickets.meta.sla")}>
            <SlaBadge sla={sla} size="sm" />
          </MetaBlock>
        )}

        <MetaBlock label={t("tickets.meta.assignee")}>
          {assigneeField && assignToMe ? (
            <AssigneeSplitChip
              field={assigneeField}
              assignToMe={assignToMe}
              ownerName={ownerName}
              withShortcut={presence}
            >
              {ownerLabel}
            </AssigneeSplitChip>
          ) : (
            <EditableChip
              field={assigneeField}
              className="border bg-card font-semibold text-muted-foreground"
            >
              {ownerLabel}
            </EditableChip>
          )}
        </MetaBlock>

        {attachments.length > 0 && (
          <MetaBlock
            label={t("tickets.meta.attachments", { count: attachments.length })}
          >
            <CollapsibleItems items={attachments}>
              {(visible) =>
                visible.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => onDownloadAttachment(attachment.id)}
                    title={attachment.name}
                    className={cn(
                      CHIP_BASE,
                      "border bg-card font-medium hover:border-secondary/40 hover:bg-secondary/10",
                    )}
                  >
                    <span className="flex shrink-0 items-center [&>svg]:h-3 [&>svg]:w-3">
                      {getAttachmentIcon(attachment.contentType)}
                    </span>
                    <span className="max-w-[180px] truncate">
                      {attachment.name}
                    </span>
                    <span className="font-normal text-muted-foreground">
                      {formatFileSize(attachment.size)}
                    </span>
                  </button>
                ))
              }
            </CollapsibleItems>
          </MetaBlock>
        )}
      </div>
      {working && (
        <div className="flex items-center gap-1.5 px-4 pb-3 text-caption text-primary">
          <PersonAvatar
            personId={working.agentId}
            name={working.displayName}
            size="xs"
            className="ring-2 ring-primary ring-offset-1 ring-offset-background"
          />
          {t("tickets.presence.working", { name: working.displayName })}
        </div>
      )}
    </header>
  );
}
